import React, { useEffect, useState } from "react";
import axios from "../../utils/axios";
import Sidebar from "./sidebar";
import { motion } from "framer-motion";
import {
  FaClock,
  FaUser,
  FaMapMarkerAlt,
  FaListUl,
  FaSearch,
} from "react-icons/fa";
import toast from "react-hot-toast";

const Supervisor = () => {
  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // 🔹 Fetch supervisor analytics
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get("/time/property-analytics");
        if (res.data.success) {
          setRecords(res.data.data);
          setFilteredRecords(res.data.data);
        } else {
          toast.error("Failed to load data");
        }
      } catch (err) {
        toast.error("Error fetching data");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // 🔹 Handle search
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredRecords(records);
    } else {
      const lower = searchTerm.toLowerCase();
      setFilteredRecords(
        records.filter(
          (r) =>
            r.name.toLowerCase().includes(lower) ||
            r.username.toLowerCase().includes(lower) ||
            r.circle.toLowerCase().includes(lower) ||
            r.pin.toLowerCase().includes(lower)
        )
      );
    }
  }, [searchTerm, records]);

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />

      <main className="flex-1 p-6 overflow-y-auto ">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800">
            Supervisor's Time Analytics
          </h1>
          <div className="relative">
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, pin, or circle..."
              className="pl-10 pr-4 py-2 border rounded-xl focus:ring-2 focus:ring-blue-400 outline-none text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Summary Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 bg-white p-5 rounded-2xl shadow-md border border-gray-100"
        >
          <h2 className="text-lg font-semibold text-gray-700 mb-2">Summary</h2>
          <p className="text-gray-500">
            Total Records:{" "}
            <span className="font-bold text-gray-800">{records.length}</span>
          </p>
        </motion.div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center py-20">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 1 }}
              className="border-4 border-blue-300 border-t-transparent rounded-full w-10 h-10"
            ></motion.div>
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredRecords.length === 0 && (
          <div className="text-center py-20 text-gray-500">
            <p className="text-lg font-medium">No matching records found.</p>
          </div>
        )}

        {/* Data Cards */}
        <motion.div
          layout
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {filteredRecords.map((item, index) => (
            <motion.div
              key={index}
              layout
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="bg-white hover:bg-blue-50 transition-all p-5 rounded-2xl shadow-sm hover:shadow-lg border border-gray-100"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <FaUser className="text-blue-500 text-xl" />
                  <div>
                    <h2 className="text-lg font-semibold text-gray-800">
                      {item.name}
                    </h2>
                    <span className="text-sm text-gray-500">@{item.username}</span>
                  </div>
                </div>
              </div>

              {/* Info Section */}
              <div className="text-gray-700 space-y-2">
                <div className="flex items-center gap-2">
                  <FaMapMarkerAlt className="text-green-500" />
                  <span className="font-medium">{item.circle}</span>
                </div>

                <div className="bg-gray-100 text-gray-700 rounded-md p-2 text-sm break-words">
                  <strong>PIN:</strong> {item.pin}
                </div>

                <div className="flex items-center gap-2 mt-2">
                  <FaListUl className="text-purple-500" />
                  <span>
                    Accesses:{" "}
                    <strong className="text-gray-800">
                      {item.total_accesses}
                    </strong>
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <FaClock className="text-orange-500" />
                  <span>
                    Time Spent:{" "}
                    <strong className="text-gray-800">
                      {item.total_time_spent}s
                    </strong>
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <FaClock className="text-blue-500" />
                  <span>
                    Last Accessed:{" "}
                    <strong className="text-gray-800">
                      {formatDate(item.last_accessed_at)}
                    </strong>
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-4 border-t pt-3 text-sm text-gray-700">
                <strong>Actions:</strong>
                <p className="text-gray-600 mt-1 italic line-clamp-3">
                  {item.action_sequence}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </main>
    </div>
  );
};

export default Supervisor;
